import React, { useEffect } from "react";
import PropTypes from "prop-types";
import Alert from "./common/alert/Alert";
import CircularProgress from "@mui/material/CircularProgress";

const EditorAlerts = ({
  i18n,
  lmsOverwriteDialog,
  lmsLogCreatedAt,
  onLmsExportConfirm,
  onLmsExportOverwrite,
  onLmsExportCancel,
  lmsExporting,
  lmsExportResult,
  fontLinkModalVisible,
  pendingFontLinks,
  fontLinkModalLoading,
  fontLinkModalResult,
  onFontLinkConfirm,
  onFontLinkCancel,
  localDraftPrompt,
  onLocalDraftDecision,
}) => {
  const fontLinkSuccessResult = fontLinkModalResult && fontLinkModalResult.success;

  useEffect(() => {
    if (fontLinkModalVisible && fontLinkModalResult && !fontLinkModalResult.success) {
      onFontLinkCancel();
    }
  }, [fontLinkModalVisible, fontLinkModalResult, onFontLinkCancel]);

  return (
    <>
      <Alert
        className="pageModal"
        show={lmsOverwriteDialog}
        hasHeader
        title={
          !lmsLogCreatedAt && !lmsExportResult
            ? i18n.t("messages.export_to_lms")
            : i18n.t("messages.lms_overwrite_title")
        }
        backdrop="static"
        cancelButton={!!lmsLogCreatedAt && !lmsExportResult}
        cancelButtonText={i18n.t("messages.Cancel")}
        acceptButtonText={
          lmsExportResult
            ? i18n.t("messages.okay")
            : !lmsLogCreatedAt
              ? i18n.t("messages.okay")
              : i18n.t("messages.lms_overwrite")
        }
        acceptButtonDisabled={!!lmsExporting}
        secondaryButton={!!lmsLogCreatedAt && !lmsExportResult}
        secondaryButtonText={i18n.t("messages.lms_save_as")}
        secondaryButtonDisabled={!!lmsExporting}
        onSecondaryButton={() => onLmsExportOverwrite()}
        onClose={(confirmed) => {
          if (lmsExportResult) {
            onLmsExportCancel();
            return;
          }
          if (confirmed) {
            onLmsExportConfirm();
          } else {
            if (lmsExporting) {
              document.body.dispatchEvent(new Event("canceled"));
            }
            onLmsExportCancel();
          }
        }}
      >
        {lmsExportResult ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px 0",
              gap: 12,
            }}
          >
            <i
              className="material-icons"
              style={{
                fontSize: 84,
                color: lmsExportResult.success ? "#16a34a" : "#dc2626",
              }}
            >
              {lmsExportResult.success ? "check_circle" : "error"}
            </i>
            <p style={{ margin: 0, color: "#374151", fontSize: 14, textAlign: "center" }}>
              {lmsExportResult.message}
            </p>
          </div>
        ) : (
          <>
            {!lmsLogCreatedAt ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "24px 0",
                  gap: 16,
                }}
              >
                <CircularProgress size={32} thickness={4} />
                <span style={{ color: "#6B7280", fontSize: 16 }}>
                  {i18n.t("messages.export_to_lms")}...
                </span>
              </div>
            ) : (
              <>
                <p>
                  {i18n.t("messages.lms_overwrite_message", {
                    date: new Date(lmsLogCreatedAt || "").toLocaleDateString("tr-TR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                  })}
                </p>
                <p>{i18n.t("messages.lms_overwrite_message_second")}</p>
                {lmsExporting && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginTop: 8,
                      color: "#6B7280",
                      fontSize: 13,
                    }}
                  >
                    <CircularProgress size={16} thickness={5} />
                    <span>{i18n.t("messages.export_to_lms")}...</span>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Alert>

      <Alert
        className="pageModal font-link-modal"
        show={fontLinkModalVisible}
        hasHeader
        title={i18n.t("messages.GoogleFontsModal.title")}
        backdrop="static"
        cancelButton={!fontLinkModalLoading && !fontLinkSuccessResult}
        cancelButtonText={i18n.t("messages.GoogleFontsModal.cancel")}
        acceptButtonText={
          fontLinkSuccessResult
            ? i18n.t("messages.okay")
            : i18n.t("messages.GoogleFontsModal.approve")
        }
        acceptButtonDisabled={!!fontLinkModalLoading}
        onClose={(confirmed) => {
          if (fontLinkSuccessResult) {
            onFontLinkCancel();
            return;
          }
          if (confirmed) {
            onFontLinkConfirm();
          } else {
            onFontLinkCancel();
          }
        }}
      >
        <style>{`
          .font-link-modal {
            z-index: 10000 !important;
          }
          .font-link-modal + .modal-backdrop {
            z-index: 9999 !important;
          }
          .font-link-modal__content {
            padding: 16px;
          }
        `}</style>

        {fontLinkSuccessResult ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px 0",
              gap: 12,
            }}
          >
            <i
              className="material-icons"
              style={{
                fontSize: 84,
                color: "#16a34a",
              }}
            >
              {"check_circle"}
            </i>
            <p style={{ margin: 0, color: "#374151", fontSize: 14, textAlign: "center" }}>
              {fontLinkSuccessResult.message}
            </p>
          </div>
        ) : fontLinkModalLoading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px 0",
              gap: 16,
            }}
          >
            <CircularProgress size={32} thickness={4} />
            <span style={{ color: "#6B7280", fontSize: 16 }}>
              {i18n.t("messages.GoogleFontsModal.loading") || "Lütfen bekleyin..."}
            </span>
          </div>
        ) : (
          <div className="font-link-modal__content">
            <p>{i18n.t("messages.GoogleFontsModal.fontsNotFound")}</p>
            <ul>
              {pendingFontLinks.map((fontName, index) => (
                <li key={index}>
                  <strong>{fontName}</strong>
                </li>
              ))}
            </ul>
            <p>{i18n.t("messages.GoogleFontsModal.needGoogleFonts")}</p>
            <p>
              <strong>{i18n.t("messages.GoogleFontsModal.importantInfo")}</strong>
            </p>
            <ul>
              <li>{i18n.t("messages.GoogleFontsModal.connectsToGoogle")}</li>
              <li>{i18n.t("messages.GoogleFontsModal.ipAddressVisible")}</li>
              <li>{i18n.t("messages.GoogleFontsModal.secureLoading")}</li>
              <li>{i18n.t("messages.GoogleFontsModal.safeForViewing")}</li>
              <li>{i18n.t("messages.GoogleFontsModal.editorAndExport")}</li>
            </ul>
            <p>{i18n.t("messages.GoogleFontsModal.permissionQuestion")}</p>
          </div>
        )}
      </Alert>

      {localDraftPrompt && (
        <Alert
          className="pageModal"
          show
          hasHeader
          backdrop="static"
          title={
            localDraftPrompt.phase === "choose"
              ? i18n.t("localDraft.title")
              : i18n.t("localDraft.discardTitle")
          }
          cancelButton
          cancelButtonText={
            localDraftPrompt.phase === "choose"
              ? i18n.t("localDraft.useServer")
              : i18n.t("localDraft.keep")
          }
          acceptButtonText={
            localDraftPrompt.phase === "choose"
              ? i18n.t("localDraft.open")
              : i18n.t("localDraft.discard")
          }
          onClose={(confirmed) => onLocalDraftDecision(confirmed)}
        >
          {localDraftPrompt.phase === "choose" ? (
            <>
              <p>
                {i18n.t("localDraft.body", {
                  savedAt: new Date(localDraftPrompt.draft.savedAt).toLocaleString(),
                })}
              </p>
              {localDraftPrompt.serverNewer && <p>{i18n.t("localDraft.serverNewer")}</p>}
            </>
          ) : (
            <p>{i18n.t("localDraft.discardBody")}</p>
          )}
        </Alert>
      )}
    </>
  );
};

EditorAlerts.propTypes = {
  i18n: PropTypes.object.isRequired,
  lmsOverwriteDialog: PropTypes.bool.isRequired,
  lmsLogCreatedAt: PropTypes.any,
  onLmsExportConfirm: PropTypes.func.isRequired,
  onLmsExportOverwrite: PropTypes.func.isRequired,
  onLmsExportCancel: PropTypes.func.isRequired,
  lmsExporting: PropTypes.bool,
  lmsExportResult: PropTypes.object,
  fontLinkModalVisible: PropTypes.bool.isRequired,
  pendingFontLinks: PropTypes.arrayOf(PropTypes.string).isRequired,
  fontLinkModalLoading: PropTypes.bool,
  fontLinkModalResult: PropTypes.object,
  onFontLinkConfirm: PropTypes.func.isRequired,
  onFontLinkCancel: PropTypes.func.isRequired,
  localDraftPrompt: PropTypes.object,
  onLocalDraftDecision: PropTypes.func.isRequired,
};

export default EditorAlerts;
